/**
 * Whether this machine can be asked to do more work — decided, as it has always
 * been, by counting CPU cores.
 *
 * The number is bad and this module does not pretend otherwise.
 * `navigator.hardwareConcurrency` is frequently capped or spoofed by the
 * browser, says nothing about thermal throttling or battery, and is sampled
 * once at startup — so a throttled eight-core laptop is "strong" forever and a
 * capped browser reports "weak" on a machine that is fine.
 *
 * What this module fixes is narrower and real: there were FOUR copies of the
 * check with TWO different thresholds and no shared constant, so a 3-core
 * machine was "strong" for publishing and "weak" for recording in the same
 * session, and nobody could have told you that without grepping.
 *
 * The two thresholds are kept apart deliberately. Publishing three simulcast
 * encodes and locally compositing a whole grid into one recorded stream are
 * different loads, and folding them into one number would silently change
 * recording behaviour on 3- and 4-core machines to fix a tidiness problem.
 * Where the real lines sit needs measurements from actual calls, which we do
 * not have — see VIDEO_TRANSMISSION_ANALYSIS.md §8.3.
 */

/** Assume mid-range when the browser declines to say. */
const ASSUMED_CORES = 4

/**
 * Publishing. At or below this we stop asking for simulcast and floor the
 * capture. Mirrored in priv/static/portal/video.html — see the SHARED marker
 * there; device-capability.test.ts asserts the two agree.
 */
export const WEAK_DEVICE_MAX_CORES = 2

/**
 * Local recording, which composites every tile and encodes the result. Looser
 * on purpose: this is the heavier job, so more machines count as weak for it.
 */
export const RECORDING_WEAK_DEVICE_MAX_CORES = 4

/** Read once per call rather than cached, so a test can stub it. */
export function cpuCores(): number {
  return navigator.hardwareConcurrency || ASSUMED_CORES
}

export function isWeakDevice(): boolean {
  return cpuCores() <= WEAK_DEVICE_MAX_CORES
}

export function isWeakForRecording(): boolean {
  return cpuCores() <= RECORDING_WEAK_DEVICE_MAX_CORES
}

/**
 * A phone browser (Android phone, iPhone) — tablets and desktops are not.
 *
 * By user agent, read once per call like cpuCores. Core count cannot answer
 * this: the phones measured for heat report 8 cores and pass isWeakDevice.
 */
export function isPhone(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return /iPhone|iPod/i.test(ua) || (/Android/i.test(ua) && /Mobile/i.test(ua))
}
